import React, { useState, useEffect } from 'react';
import Hero from '../components/Hero';
import { PhoneIcon, EmailIcon, LocationIcon, ClockIcon } from '../components/Icons';
import { getHeroes, getCompanyInfo, submitContactForm } from '../services/api';
import './Contact.css';

const Contact = () => {
  const [hero, setHero] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroData, companyData] = await Promise.all([
          getHeroes(),
          getCompanyInfo()
        ]);
        setHero(heroData.contact);
        setCompanyInfo(companyData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await submitContactForm(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    'Audio/Video Systems',
    'CCTV & Security',
    'Smart Automation',
    'Software Development',
    'Other'
  ];

  return (
    <div className="contact-page">
      <Hero
        title="Contact Us"
        subtitle="Get in touch for a free consultation"
        backgroundImage={hero?.image_url}
        height="small"
        overlay={0.7}
      />

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Let's Talk</h2>
              <p>
                Ready to start your project? Have questions about our services? 
                We'd love to hear from you. Fill out the form or reach us directly.
              </p>

              <div className="info-items">
                {companyInfo.phone && (
                  <div className="info-item">
                    <div className="info-icon">
                      <PhoneIcon size={24} color="#FF5722" />
                    </div>
                    <div>
                      <span className="info-label">Phone</span>
                      <a href={`tel:${companyInfo.phone}`}>{companyInfo.phone}</a>
                    </div>
                  </div>
                )}

                {companyInfo.email && (
                  <div className="info-item">
                    <div className="info-icon">
                      <EmailIcon size={24} color="#FF5722" />
                    </div>
                    <div>
                      <span className="info-label">Email</span>
                      <a href={`mailto:${companyInfo.email}`}>{companyInfo.email}</a>
                    </div>
                  </div>
                )}

                {(companyInfo.address || companyInfo.city) && (
                  <div className="info-item">
                    <div className="info-icon">
                      <LocationIcon size={24} color="#FF5722" />
                    </div>
                    <div>
                      <span className="info-label">Location</span>
                      <span>
                        {companyInfo.address && `${companyInfo.address}, `}
                        {companyInfo.city && `${companyInfo.city}, `}
                        {companyInfo.state}
                      </span>
                    </div>
                  </div>
                )}

                <div className="info-item">
                  <div className="info-icon">
                    <ClockIcon size={24} color="#FF5722" />
                  </div>
                  <div>
                    <span className="info-label">Hours</span>
                    <span>
                      {companyInfo.hours_weekdays || 'Mon-Fri: 9AM - 6PM'}<br />
                      {companyInfo.hours_saturday || 'Sat: By Appointment'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="social-links">
                {companyInfo.facebook && (
                  <a href={companyInfo.facebook} target="_blank" rel="noopener noreferrer" className="social-link">Facebook</a>
                )}
                {companyInfo.instagram && (
                  <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer" className="social-link">Instagram</a>
                )}
                {companyInfo.linkedin && (
                  <a href={companyInfo.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">LinkedIn</a>
                )}
              </div>
            </div>

            <div className="contact-form-wrapper">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h3>Thank You!</h3>
                  <p>We've received your message and will get back to you within 24 hours.</p>
                  <button onClick={() => setSubmitted(false)} className="btn btn-primary">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h3>Send Us a Message</h3>
                  
                  {error && <div className="error-message">{error}</div>}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="service">Service Interested In</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                      >
                        <option value="">Select a service</option>
                        {services.map(service => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
